'use client';

import { useEffect, useMemo, useState } from 'react';
import { Check, CheckCircle2, FileText, Loader2, Search } from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/ui/status-badge';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/utils';
import { formatFileSize } from '@/lib/utils/file-utils';
import { searchMigrationFilesAction } from '@/lib/services/smokeball-migration/actions';
import type { SmokeballFile } from '@/lib/services/smokeball-migration/types';
import {
  attachMatterDocument,
  type PmsImportFileType
} from '@/lib/services/redemption/pms-import';

/**
 * Redemption "Attach from Smokeball" (LF-5952) — single-select picker for
 * attaching one PMS document to a Prepare-sheet requirement. Lists the matter's
 * PMS documents (reusing the document-search endpoint) and, on attach, calls
 * the generic import endpoint with the row's `target`. Only mounted when the
 * matter is PMS-connected (see the gate in `prepare-requirements-sheet.tsx`).
 */
export interface PmsImportTarget {
  /** Human label for the dialog copy, e.g. "the Grant of Representation", "Helen Miller's ID". */
  label: string;
  fileType: PmsImportFileType;
  contactId?: number;
  institutionId?: number;
  institutionFormId?: number;
  signatoryContactId?: number;
  inventoryItemId?: number;
  attachmentId?: number;
}

interface AttachFromPmsDialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: number;
  providerLabel: string;
}

interface AttachFromPmsRequirementProps extends AttachFromPmsDialogBaseProps {
  target: PmsImportTarget;
  /**
   * Fired after the import lands, with the new Vale `fileId` **and the PMS
   * document it came from**. Target-mode callers (redemption) ignore both and
   * just refresh; callback-mode callers (ADOC-7, LF-6245) reference the imported
   * file by this id, exactly like a choose-existing pick.
   *
   * `file` is what lets the receiving surface name the document (LF-6261): the
   * import is brand new, so it is in no cached matter-files list yet and an
   * id-only hand-off can only fall back to `File <id>`.
   */
  onAttached: (fileId: number, file: SmokeballFile) => void | Promise<void>;
  landing?: never;
}

interface AttachFromPmsCustomLandingProps extends AttachFromPmsDialogBaseProps {
  target?: never;
  onAttached?: never;
  /**
   * A landing that is **not** a requirement attach (VALE-1124).
   *
   * The picking half of this dialog — list the matter's PMS documents, filter,
   * select one — is the same wherever the documents are going; only what happens
   * on confirm differs. The correspondence import is the case that needed this:
   * it does not file a Vale document at all, it parses the bytes into a thread as
   * a correspondence, so `attachMatterDocument` is the wrong landing rather than a
   * configurable one. Everything else (search, empty states, "Already in Vale",
   * error surface) stays shared instead of being re-typed next door.
   */
  landing: {
    /**
     * Let the user pick several documents rather than one (VALE-1198).
     *
     * Opt-in, and offered on this variant only: a requirement target attaches ONE document
     * by definition, so there is nothing for the shipped Prepare-sheet callers to choose
     * between. The correspondence thread is the opposite — the hand-upload path beside it
     * takes several files at once, so a single-select picker there means opening the dialog
     * once per letter, which is the tedium the PMS source existed to remove.
     */
    multiple?: boolean;
    /** Confirm verb, capitalised — titles the dialog ("{verb} from {provider}") and the button. */
    verb: string;
    /** The same verb as a present participle for the in-flight button ("Importing…"). Spelled out rather than derived, because English does not suffix reliably. */
    busyVerb: string;
    /** The single line under the title. Says where the document is going. */
    description: string;
    /**
     * Runs on confirm. Returns the success toast to show, or nothing to stay
     * silent — which is what a caller that narrates the outcome itself wants (an
     * import that turned out to be a duplicate is not a "Imported ✓" toast).
     * Throwing surfaces the message inside the dialog and leaves it open.
     */
    run: (file: SmokeballFile) => Promise<string | void>;
  };
}

export type AttachFromPmsDialogProps =
  | AttachFromPmsRequirementProps
  | AttachFromPmsCustomLandingProps;

/**
 * Run the landing over every picked document, in order, and say once what happened.
 *
 * **Sequential, deliberately.** It mirrors `useThreadImport.importFiles`, which files a
 * multi-file drop one at a time: each document is a separate server-side download plus a
 * separate parse, and running them concurrently means N thread invalidations racing each
 * other while the user watches. Doing it in the same order the same way is also what makes
 * a PMS import indistinguishable from a drop, which is the property the feature is built on.
 *
 * **A refusal does not stop the rest**, for the same reason `importFiles` does not: the
 * documents are independent, and abandoning four good ones because the third was unreadable
 * would be a worse answer than filing them and saying so. The caller narrates each outcome
 * on its own surface; what comes back here is only the summary line.
 *
 * Throws only when EVERY document failed — there is nothing to celebrate and the dialog
 * should stay open with the reason, which is exactly what a single-pick failure does today.
 */
async function runLanding(
  landing: { run: (file: SmokeballFile) => Promise<string | void> },
  picked: SmokeballFile[]
): Promise<void> {
  const failures: string[] = [];
  const messages: string[] = [];

  for (const file of picked) {
    try {
      const message = await landing.run(file);
      // A landing returns nothing when it is narrating the outcome itself — an import that
      // turned out to be a duplicate is not a success to count, nor a failure to report.
      if (message) messages.push(message);
    } catch (err) {
      failures.push(
        err instanceof Error
          ? `${file.fileName}: ${err.message}`
          : file.fileName
      );
    }
  }

  if (failures.length === picked.length) {
    throw new Error(failures.join('\n'));
  }

  if (messages.length === 1) {
    toast.success(messages[0]);
  } else if (messages.length > 1) {
    toast.success(`Imported ${messages.length} documents`);
  }

  // Some landed, some did not — the count is the honest headline, and the per-document
  // detail is already on the surface the caller owns.
  if (failures.length > 0) {
    toast.error(
      failures.length === 1
        ? `Couldn’t import ${failures[0]}`
        : `Couldn’t import ${failures.length} of ${picked.length} documents`
    );
  }
}

export function AttachFromPmsDialog(props: AttachFromPmsDialogProps) {
  const { open, onOpenChange, matterId, providerLabel, landing } = props;
  const verb = landing?.verb ?? 'Attach';
  const busyVerb = landing?.busyVerb ?? 'Attaching';
  const [isSearching, setIsSearching] = useState(true);
  const [files, setFiles] = useState<SmokeballFile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  // Always a list; single-select is simply the case where picking replaces rather than
  // appends. Keeping one state shape avoids a second source of truth for "what is chosen"
  // and keeps every downstream read (`isSelected`, the confirm guard) identical in both modes.
  const [selected, setSelected] = useState<SmokeballFile[]>([]);
  const [isAttaching, setIsAttaching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowMultiple = landing?.multiple ?? false;
  const isSelected = (file: SmokeballFile) =>
    selected.some((f) => f.externalId === file.externalId);
  /**
   * Picking a row. In multiple mode this toggles membership; otherwise it replaces the
   * selection, which is what keeps the shipped requirement callers behaving exactly as before.
   *
   * Selection is held as the file objects rather than as ids into the filtered list, so
   * typing in the filter never silently drops something already picked.
   */
  const togglePick = (file: SmokeballFile) =>
    setSelected((prev) => {
      if (!allowMultiple) return [file];
      return prev.some((f) => f.externalId === file.externalId)
        ? prev.filter((f) => f.externalId !== file.externalId)
        : [...prev, file];
    });

  useEffect(() => {
    if (!open) return;

    const fetchFiles = async () => {
      setIsSearching(true);
      setError(null);
      setSelected([]);
      setSearchQuery('');

      const result = await searchMigrationFilesAction(matterId);
      if (result.success && result.data) {
        setFiles(result.data.files ?? []);
      } else {
        setError(result.error ?? 'Failed to load documents');
      }
      setIsSearching(false);
    };

    void fetchFiles();
  }, [open, matterId]);

  const handleAttach = async () => {
    const [first] = selected;
    if (!first) return;
    setIsAttaching(true);
    setError(null);
    try {
      if (landing) {
        await runLanding(landing, selected);
        onOpenChange(false);
        return;
      }
      const { target, onAttached } = props;
      const { fileId } = await attachMatterDocument(matterId, {
        source: 'pms',
        externalId: first.externalId,
        fileName: first.fileName,
        fileType: target.fileType,
        ...(target.contactId != null && { contactId: target.contactId }),
        ...(target.institutionId != null && {
          institutionId: target.institutionId
        }),
        ...(target.institutionFormId != null && {
          institutionFormId: target.institutionFormId
        }),
        ...(target.signatoryContactId != null && {
          signatoryContactId: target.signatoryContactId
        }),
        ...(target.inventoryItemId != null && {
          inventoryItemId: target.inventoryItemId
        }),
        ...(target.attachmentId != null && {
          attachmentId: target.attachmentId
        })
      });
      toast.success(`Attached ${first.fileName} from ${providerLabel}`);
      await onAttached(fileId, first);
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${verb.toLowerCase()} document`
      );
    } finally {
      setIsAttaching(false);
    }
  };

  const filteredFiles = useMemo(
    () =>
      searchQuery
        ? files.filter((f) =>
            f.fileName.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : files,
    [files, searchQuery]
  );

  const noneOnMatter = !isSearching && files.length === 0;
  const noFilterMatch =
    !isSearching && files.length > 0 && filteredFiles.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {verb} from {providerLabel}
          </DialogTitle>
          <DialogDescription>
            {props.landing
              ? props.landing.description
              : `Choose a document from ${providerLabel} to attach as ${props.target.label}.`}
          </DialogDescription>
        </DialogHeader>

        {!isSearching && files.length > 0 && (
          <div className="relative">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Filter documents by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        )}

        <div className="min-h-48">
          {isSearching && (
            <div className="rounded-lg border border-dashed p-12 text-center">
              <Loader2 className="text-foreground-subtle mx-auto mb-3 h-8 w-8 animate-spin" />
              <p className="text-muted-foreground text-sm">
                Loading documents…
              </p>
            </div>
          )}

          {(noneOnMatter || noFilterMatch) && (
            <div className="rounded-lg border border-dashed p-12 text-center">
              <FileText className="text-foreground-subtle mx-auto mb-3 h-12 w-12" />
              <p className="text-muted-foreground text-sm">
                {noFilterMatch
                  ? 'No documents match your filter'
                  : 'No documents found on this matter'}
              </p>
            </div>
          )}

          {filteredFiles.length > 0 && (
            <div className="max-h-96 overflow-y-auto rounded-lg border">
              <Table>
                <TableBody>
                  {filteredFiles.map((file) => {
                    const picked = isSelected(file);
                    return (
                      <TableRow
                        key={file.externalId}
                        className={cn(
                          'cursor-pointer',
                          picked
                            ? 'bg-surface-subtle hover:bg-info-subtle'
                            : 'hover:bg-muted/50'
                        )}
                        onClick={() => togglePick(file)}
                        // The row IS the control, so it carries the state a screen reader
                        // needs; the marker below is decorative.
                        role={allowMultiple ? 'checkbox' : 'radio'}
                        aria-checked={picked}
                      >
                        <TableCell width={40}>
                          {/* Square when several may be chosen, round when only one may —
                              the shape is the only thing telling a user which this is, and
                              a round marker that accepts a second selection reads as a bug
                              rather than a feature (VALE-1198). */}
                          <span
                            className={cn(
                              'flex h-4 w-4 items-center justify-center border',
                              allowMultiple ? 'rounded-[4px]' : 'rounded-full',
                              picked
                                ? 'border-info bg-info text-info-foreground'
                                : 'border-border-strong'
                            )}
                          >
                            {picked && <Check className="h-3 w-3" />}
                          </span>
                        </TableCell>
                        <TableCell className="flex items-center gap-2">
                          <FileText className="text-muted-foreground h-4 w-4" />
                          {/* Same badge, tone and wording the Extract picker uses
                              (`smokeball-search-step.tsx`), and inside the name's
                              span for the same reason: as a flex sibling it detaches
                              from the name it annotates when the name wraps
                              (VALE-89). Unlike Extract this picker still allows the
                              pick — attaching an already-in-Vale document to a
                              requirement is a real workflow, and since VALE-358 it
                              no longer produces a document Vale can push back into
                              the PMS. */}
                          <span
                            className={cn(
                              'min-w-0',
                              picked ? 'font-semibold' : 'font-medium'
                            )}
                          >
                            {file.fileName}
                            {file.alreadyMigrated && (
                              <StatusBadge
                                tone="neutral"
                                icon={CheckCircle2}
                                className="ml-2 align-middle whitespace-nowrap"
                              >
                                Already in Vale
                              </StatusBadge>
                            )}
                          </span>
                        </TableCell>
                        <TableCell width={90} className="text-center text-sm">
                          {file.sizeBytes
                            ? formatFileSize(file.sizeBytes)
                            : 'n/a'}
                        </TableCell>
                        <TableCell width={135} className="text-right text-sm">
                          {formatDate(file.dateCreated)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {error && (
            <div className="border-destructive/50 bg-destructive/10 mt-4 rounded-lg border p-3">
              <p className="text-destructive text-sm">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            size="xs"
            density="compact"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isAttaching}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            density="compact"
            gap="standard"
            onClick={handleAttach}
            disabled={selected.length === 0 || isAttaching}
          >
            {isAttaching ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {busyVerb}…
              </>
            ) : (
              // The count only appears once there is a choice to have made — "Import 1"
              // on a single-select picker would be noise about a decision the user never
              // faced.
              `${verb}${allowMultiple && selected.length > 1 ? ` ${selected.length}` : ''}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
